import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  TotalSpendRequest,
  TotalSpendResponse,
  ProvidersBreakdownRequest,
  ProvidersBreakdownResponse,
  ContributorsSpendRequest,
  ContributorsSpendResponse,
  SpendRequest,
  SpendResponse,
  CustomDashboardResponse,
  CustomDashboardsListResponse,
  CreateCustomDashboardRequest,
  UpdateCustomDashboardRequest,
} from '@/data/types/dashboard.types'

async function getTotalSpend(request: TotalSpendRequest): Promise<TotalSpendResponse> {
  return fetchWithAuth('/api/v1/dashboards/total-spend', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export function useTotalSpend(request: TotalSpendRequest | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['totalSpend', request],
    queryFn: () => getTotalSpend(request!),
    enabled: enabled && request !== null,
  })
}

async function getProvidersBreakdown(request: ProvidersBreakdownRequest): Promise<ProvidersBreakdownResponse> {
  return fetchWithAuth('/api/v1/dashboards/providers-breakdown', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export function useProvidersBreakdown(request: ProvidersBreakdownRequest | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['providersBreakdown', request],
    queryFn: () => getProvidersBreakdown(request!),
    enabled: enabled && request !== null,
  })
}

async function getContributorsSpend(request: ContributorsSpendRequest): Promise<ContributorsSpendResponse> {
  return fetchWithAuth('/api/v1/dashboards/contributors-spend', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export function useContributorsSpend(request: ContributorsSpendRequest | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['contributorsSpend', request],
    queryFn: () => getContributorsSpend(request!),
    enabled: enabled && request !== null,
  })
}

async function getSpend(request: SpendRequest): Promise<SpendResponse> {
  return fetchWithAuth('/api/v1/dashboards/spend', {
    method: 'POST',
    body: JSON.stringify(request),
  })
}

export function useSpend(request: SpendRequest | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['dashboardSpend', request],
    queryFn: () => getSpend(request!),
    enabled: enabled && request !== null,
  })
}

/**
 * Custom Dashboards Hooks
 */

export interface CustomDashboardsListParams {
  is_recommended?: boolean | null
}

export function useCustomDashboards(params?: CustomDashboardsListParams, enabled: boolean = true) {
  const queryParams = new URLSearchParams()
  if (params?.is_recommended !== undefined && params?.is_recommended !== null) {
    queryParams.append('is_recommended', String(params.is_recommended))
  }
  const queryString = queryParams.toString()
  const endpoint = `/api/v1/custom-dashboards${queryString ? `?${queryString}` : ''}`

  return useQuery<CustomDashboardsListResponse>({
    queryKey: ['custom-dashboards', params],
    queryFn: () => fetchWithAuth(endpoint),
    enabled,
  })
}

export function useCustomDashboardDetail(dashboardId: string, enabled: boolean = true) {
  return useQuery<CustomDashboardResponse>({
    queryKey: ['custom-dashboards', dashboardId],
    queryFn: () => fetchWithAuth(`/api/v1/custom-dashboards/${dashboardId}`),
    enabled: enabled && !!dashboardId && dashboardId !== 'new',
  })
}

export function useCreateCustomDashboard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCustomDashboardRequest) =>
      fetchWithAuth('/api/v1/custom-dashboards', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards'] })
    },
  })
}

export function useUpdateCustomDashboard(dashboardId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateCustomDashboardRequest) =>
      fetchWithAuth(`/api/v1/custom-dashboards/${dashboardId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards'] })
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards', dashboardId] })
      queryClient.setQueryData(['custom-dashboards', dashboardId], data)
    },
  })
}

export function useDeleteCustomDashboard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dashboardId: string) =>
      fetchWithAuth(`/api/v1/custom-dashboards/${dashboardId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards'] })
    },
  })
}

export function useStarCustomDashboard() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ dashboardId, star }: { dashboardId: string; star: boolean }) =>
      fetchWithAuth(`/api/v1/custom-dashboards/${dashboardId}/star`, {
        method: star ? 'POST' : 'DELETE',
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards'] })
      queryClient.invalidateQueries({ queryKey: ['custom-dashboards', variables.dashboardId] })
    },
  })
}

